/**
 * useIngestReservation — TanStack mutation hook over
 * `POST /organizations/{slug}/reservations/ingest` (SCRUM-350).
 *
 * Records a LIVE, non-integrated channel booking (a real OTA booking that did
 * NOT arrive via the channel — failed sync / channel-account change) through
 * the ALREADY-SHIPPED ingest path (SCRUM-275/277). The endpoint / FSM /
 * channel-remittance snapshot are UNCHANGED by this ticket; this hook is a new
 * manual-entry FE caller.
 *
 * The shape MIRRORS `useCreateReservation`, with three deliberate differences:
 *
 *   1. **Single-envelope unwrap.** The ingest endpoint returns
 *      `{ data: ReservationLifecycleResultDto }` (NOT the double envelope the
 *      create POST uses) — so the mutationFn resolves `result.data.data`.
 *
 *   2. **No `notFound` kind.** The ingest endpoint declares no 404, so the
 *      tagged error union is `forbidden` (403) | `conflict` (409) | `illegal`
 *      (422) | `error` (any other non-ok) — there is NO `notFound`.
 *
 *   3. **1:1 passthrough body.** `IngestInput` maps straight onto the wire
 *      `IngestReservationDto`. `netRemittanceCents` arrives ALREADY computed
 *      by the form's Zod money field (via `dollarsToCents` from
 *      `@artemis/validators`, the single conversion path — CLAUDE.md FE rule
 *      §4/§6); this hook does NO money math. The manual caller OMITS every
 *      SCRUM-367 automated-channel-path field (`heardAbout`, `currency`,
 *      `collectionModel`, `sourceVersion`, `sourceVersionTiebreak`,
 *      `asReported`, `channelId`, `rawPayload`). `channelReservationId` is
 *      sent as literal `null` when blank (keyless → CREATE-only), never
 *      `undefined`, never an empty string.
 *
 * On success `await`s `invalidateQueries` for `bookingQueryKeys.lists()` so the
 * grid picks up the new / reconciled row. NEVER fakes success; NEVER
 * optimistically inserts a row.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ReservationLifecycleResultDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * Tagged error kinds the form branches on. NO `notFound` — the ingest
 * endpoint declares no 404 (unlike `useCreateReservation`).
 */
export type IngestErrorKind =
  | 'forbidden' // 403 — lacks `booking.reservations.ingest` / per-property access
  | 'conflict' // 409 — idempotency / calendar-lock / OCC race
  | 'illegal' // 422 — validation (missing netRemittance / bad status / …)
  | 'error'; // any other non-ok (incl. an unexpected 400/500)

/** An ingest failure carrying the mapped `kind` for the UI. */
export interface IngestError extends Error {
  readonly kind: IngestErrorKind;
  /**
   * The machine `error` code from the response body (booking's error filter
   * emits `{ error: '<code>' }`), e.g. `channel_collect_not_locally_creatable`,
   * `unknown_reservation_type`. Lets the form show a SPECIFIC message per 422
   * cause; `undefined` when the body carries no code.
   */
  readonly code?: string;
}

/**
 * The form-facing ingest input (mirrors `CreateInput`). NOT a wire shape — the
 * hook's input surface. `reservationTypeKey` + `processor` come from the
 * SELECTED channel reservation-type (the row's `internalKey` +
 * `channelProcessor`); `netRemittanceCents` is the parsed money value
 * (`dollarsToCents(...)`, computed by the form, NOT here).
 */
export interface IngestInput {
  readonly propertyId: string;
  readonly reservationTypeKey: string;
  readonly processor: string;
  /** Keyless (blank form field) → literal `null` on the wire. */
  readonly channelReservationId: string | null;
  readonly arrivalDate: string;
  readonly departureDate: string;
  /** LIVE blocking statuses only (the form offers exactly these three). */
  readonly status: 'BKD' | 'CI' | 'CO';
  /** Already-computed cents (form owns the `dollarsToCents` conversion). */
  readonly netRemittanceCents: number;
  /**
   * Total guest occupancy. Optional, integer ≥ 1 — mirrors the wire
   * `ingestReservationSchema.guestCount` (`z.number().int().min(1).optional()`).
   * The BE maps it to `Party.create({ adults: Math.max(1, guestCount ?? 1) })`
   * and the snapshot occupancy; omit when the form leaves it blank (the wire
   * defaults occupancy to 1).
   */
  readonly guestCount?: number;
  /** Inline new-guest binding arm. */
  readonly guest: {
    readonly displayName: string;
    readonly email: string | null;
    readonly phone: string | null;
  };
  /**
   * SCRUM-382 — operator-supplied Channel Reference Notes (informational). The
   * MANUAL counterpart to the channel-transcription `asReported`: hand-entered
   * gross / OTA commission / channel-remitted taxes (ALREADY cents — the form
   * owns the `dollarsToCents` conversion) + a free-text note. Omitted entirely
   * when the operator leaves the section blank. Provenance is server-stamped
   * `'operator-supplied'`. Strictly informational — never re-priced, never touches
   * the ledger / $0-balance invariant.
   */
  readonly channelReferenceNotes?: {
    readonly grossGuestPaymentCents?: number;
    readonly otaCommissionCents?: number;
    readonly channelRemittedTaxCents?: number;
    readonly note?: string;
  };
}

/**
 * The single-unwrapped ingest result — an alias of the generated wire DTO
 * (`ReservationLifecycleResultDto`), NOT a hand-redefined shape (CLAUDE.md FE
 * rule §4). Carries `outcome` (`created|updated|noop|overbooked|stale`),
 * `reservationId`, and the optional `status` / `snapshotVersion` / required
 * `version`. The form renders all four success/quarantine outcomes honestly
 * off `outcome`.
 */
export type IngestResult = ReservationLifecycleResultDto;

/** Maps an HTTP status to the tagged error kind (ingest declares no 404). */
function kindFor(status: number): IngestErrorKind {
  if (status === 409) return 'conflict';
  if (status === 422) return 'illegal';
  if (status === 403) return 'forbidden';
  return 'error';
}

/** Read the machine `error` code from a booking error body (`{ error: '<code>' }`). */
function extractErrorCode(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const code = (body as { error?: unknown }).error;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Builds an `IngestError` for a mapped HTTP failure, carrying the body's code. */
function ingestError(status: number, code?: string): IngestError {
  const err = new Error(`ingest reservation failed (${status})`) as Error & {
    kind: IngestErrorKind;
    code?: string;
  };
  err.kind = kindFor(status);
  if (code !== undefined) err.code = code;
  return err;
}

/**
 * The narrow structural shape of the openapi-fetch ingest POST result the
 * unwrap reads. The endpoint returns the `{ data: <result> }` API envelope in
 * the openapi-fetch `{ data, response, error }` union; this is the minimal
 * surface the unwrap touches (typing against `typeof apiClient.POST<…>` is not
 * possible — the generic `ClientMethod` overload set has no `typeof`-applicable
 * signature).
 */
interface IngestPostResult {
  readonly response: Response;
  readonly data?: { readonly data: IngestResult } | undefined;
  /** openapi-fetch parks the parsed error body here on a non-2xx response. */
  readonly error?: unknown;
}

export function useIngestReservation(): UseMutationResult<IngestResult, IngestError, IngestInput> {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  return useMutation<IngestResult, IngestError, IngestInput>({
    mutationFn: async (input) => {
      if (orgSlug === null) throw ingestError(0);

      // 1:1 passthrough — NO automated-channel-path fields, NO money math.
      const body = {
        propertyId: input.propertyId,
        reservationTypeKey: input.reservationTypeKey,
        processor: input.processor,
        // Literal `null` when keyless — never undefined, never ''.
        channelReservationId: input.channelReservationId,
        arrivalDate: input.arrivalDate,
        departureDate: input.departureDate,
        status: input.status,
        netRemittanceCents: input.netRemittanceCents,
        // Optional occupancy — included ONLY when the form provides it; omitted
        // (never `undefined`, never `0`) when blank so the wire defaults to 1.
        ...(input.guestCount !== undefined ? { guestCount: input.guestCount } : {}),
        guest: {
          displayName: input.guest.displayName,
          email: input.guest.email,
          phone: input.guest.phone,
        },
        // SCRUM-382 — informational operator notes; included ONLY when present
        // (omitted-stable). No money math here — cents were computed by the form.
        ...(input.channelReferenceNotes !== undefined
          ? { channelReferenceNotes: input.channelReferenceNotes }
          : {}),
      };

      const result = (await apiClient.POST('/organizations/{slug}/reservations/ingest', {
        // params.path ONLY — no header params.
        params: { path: { slug: orgSlug } },
        body: body as never,
      })) as IngestPostResult;

      if (!result.response.ok || result.data === undefined) {
        throw ingestError(result.response.status, extractErrorCode(result.error));
      }

      await queryClient.invalidateQueries({
        queryKey: bookingQueryKeys.lists(),
      });

      // Single-envelope unwrap.
      return result.data.data;
    },
  });
}
